import React, { useState, useContext, useRef, useEffect } from "react";
import Ctx from "../../contexts/AppContext";
import { crd, sp, rad, type, icBox } from "../../constants/styles";
import Ic from "../common/Ic";
import { isOverdue, isExpiringSoon, getDaysRemaining, getLeaseForProperty } from "../../utils/leaseHelpers";

// Smart query engine — parses natural language and returns data-driven answers
function processQuery(q, { props, tens, pays, mts, leases, ccs, t }) {
  const ql = q.toLowerCase().trim();
  const now = new Date();
  const mNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const curMo = `${mNames[now.getMonth()]} ${now.getFullYear().toString().slice(-2)}`;

  // --- Property queries ---
  if (ql.match(/vacant|empty|available|unoccupied/)) {
    const vacant = props.filter(p => !p.occ);
    if (vacant.length === 0) return { text: "All properties are currently occupied! No vacancies.", icon: "home" };
    return { text: `You have **${vacant.length} vacant** propert${vacant.length === 1 ? "y" : "ies"}:\n\n${vacant.map(p => `\u2022 ${p.addr} \u2014 \u20B9${(p.rent || 0).toLocaleString()}/mo`).join("\n")}`, icon: "home" };
  }

  if (ql.match(/occupied|filled|rented/)) {
    const occ = props.filter(p => p.occ);
    return { text: `**${occ.length}/${props.length}** properties are occupied (${Math.round(occ.length / Math.max(props.length, 1) * 100)}% occupancy).\n\n${occ.map(p => { const te = tens.find(x => x.id === p.tid); return `\u2022 ${p.addr} \u2014 ${te?.name || "Unknown"}`; }).join("\n")}`, icon: "home" };
  }

  if (ql.match(/how many prop|total prop|properties count|number of prop/)) {
    const occ = props.filter(p => p.occ).length;
    return { text: `You have **${props.length} properties** total.\n\u2022 ${occ} occupied\n\u2022 ${props.length - occ} vacant\n\u2022 Total rent potential: \u20B9${props.reduce((s, p) => s + (p.rent || 0), 0).toLocaleString()}/mo`, icon: "home" };
  }

  // --- Tenant queries ---
  if (ql.match(/how many tenant|total tenant|tenant count/)) {
    const active = tens.filter(te => te.pid && !te.vac);
    const vacReq = tens.filter(te => te.vac);
    return { text: `You have **${tens.length} tenants** total.\n\u2022 ${active.length} active (assigned to property)\n\u2022 ${vacReq.length} vacate requested\n\u2022 ${tens.length - active.length - vacReq.length} unassigned`, icon: "users" };
  }

  if (ql.match(/verified|unverified|not verified/)) {
    const verified = tens.filter(te => te.verified);
    const unverified = tens.filter(te => !te.verified);
    if (ql.includes("unverified") || ql.includes("not verified")) {
      return { text: `**${unverified.length} unverified** tenants:\n\n${unverified.map(te => `\u2022 ${te.name} \u2014 ${te.ph}`).join("\n") || "None!"}`, icon: "shield" };
    }
    return { text: `**${verified.length}/${tens.length}** tenants are verified.\n${unverified.length > 0 ? `\n${unverified.length} still need verification.` : "\nAll tenants verified! \u2713"}`, icon: "shield" };
  }

  if (ql.match(/vacate|leaving|moving out/)) {
    const vac = tens.filter(te => te.vac);
    if (vac.length === 0) return { text: "No vacate requests at the moment. All tenants are staying.", icon: "users" };
    return { text: `**${vac.length} vacate request${vac.length > 1 ? "s" : ""}:**\n\n${vac.map(te => { const pr = props.find(p => p.id === te.pid); return `\u2022 ${te.name} \u2014 ${pr?.addr || "Unknown"}`; }).join("\n")}`, icon: "users" };
  }

  // --- Payment queries ---
  if (ql.match(/overdue|late|unpaid|not paid|pending rent|due rent|who.*hasn.*paid/)) {
    const duePays = pays.filter(p => p.st === "due");
    const overduePays = duePays.filter(p => isOverdue(p.mo));
    const list = overduePays.length > 0 ? overduePays : duePays;
    const label = overduePays.length > 0 ? "overdue" : "due";
    if (list.length === 0) return { text: "No pending or overdue payments! All rents are collected. \u2713", icon: "dollar" };
    const total = list.reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
    return { text: `**${list.length} ${label} payments** totaling **\u20B9${total.toLocaleString()}**:\n\n${list.map(p => { const te = tens.find(x => x.id === p.tid); return `\u2022 ${te?.name || "?"} \u2014 \u20B9${((p.amt || 0) + (p.com || 0)).toLocaleString()} (${p.mo})`; }).join("\n")}`, icon: "dollar" };
  }

  if (ql.match(/collected|received|paid.*this|revenue|income|how much.*collected/)) {
    const moPays = pays.filter(p => p.st === "paid" && p.mo === curMo);
    const moTotal = moPays.reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
    const allPaid = pays.filter(p => p.st === "paid");
    const allTotal = allPaid.reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
    return { text: `**${curMo} Collection:**\n\u2022 \u20B9${moTotal.toLocaleString()} collected (${moPays.length} payments)\n\n**All Time:**\n\u2022 \u20B9${allTotal.toLocaleString()} total collected (${allPaid.length} payments)`, icon: "dollar" };
  }

  if (ql.match(/pending.*approval|waiting.*approval|approve/)) {
    const pending = pays.filter(p => p.pending);
    if (pending.length === 0) return { text: "No payments waiting for approval.", icon: "check-circle" };
    return { text: `**${pending.length} payment${pending.length > 1 ? "s" : ""} awaiting approval:**\n\n${pending.map(p => { const te = tens.find(x => x.id === p.tid); return `\u2022 ${te?.name || "?"} \u2014 \u20B9${p.pending.amt?.toLocaleString()} (${p.pending.mth?.toUpperCase()})`; }).join("\n")}`, icon: "check-circle" };
  }

  // --- Maintenance queries ---
  if (ql.match(/open.*maint|pending.*maint|maintenance.*open|active.*maint|how many.*maint/)) {
    const open = mts.filter(m => m.st === "open" || m.st === "assigned");
    if (open.length === 0) return { text: "No open maintenance requests. Everything is resolved! \u2713", icon: "wrench" };
    const high = open.filter(m => m.urg === "high");
    return { text: `**${open.length} open maintenance** request${open.length > 1 ? "s" : ""}${high.length > 0 ? ` (${high.length} HIGH urgency!)` : ""}:\n\n${open.map(m => { const te = tens.find(x => x.id === m.tid); return `\u2022 ${m.desc} \u2014 ${te?.name || "?"} [${m.urg}/${m.st}]`; }).join("\n")}`, icon: "wrench" };
  }

  // --- Lease queries ---
  if (ql.match(/expir.*lease|lease.*expir|renew/)) {
    const expiring = (leases || []).filter(l => isExpiringSoon(l));
    if (expiring.length === 0) return { text: "No leases expiring soon. All leases are in good standing.", icon: "file-text" };
    return { text: `**${expiring.length} lease${expiring.length > 1 ? "s" : ""} expiring soon:**\n\n${expiring.map(l => { const te = tens.find(x => x.id === l.tenant_id); const days = getDaysRemaining(l); return `\u2022 ${te?.name || "?"} \u2014 ${days} days remaining (${l.end_date})`; }).join("\n")}`, icon: "file-text" };
  }

  // --- Summary / dashboard ---
  if (ql.match(/summary|overview|dashboard|status|how.*doing|report/)) {
    const occ = props.filter(p => p.occ).length;
    const due = pays.filter(p => p.st === "due");
    const dueAmt = due.reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
    const collected = pays.filter(p => p.st === "paid" && p.mo === curMo).reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
    const openM = mts.filter(m => m.st === "open" || m.st === "assigned").length;
    const expL = (leases || []).filter(l => isExpiringSoon(l)).length;
    return { text: `**\uD83D\uDCCA ${curMo} Summary:**\n\n\uD83C\uDFE0 Properties: ${occ}/${props.length} occupied\n\uD83D\uDC65 Tenants: ${tens.filter(te => te.pid).length} active\n\u2705 Collected: \u20B9${collected.toLocaleString()}\n\u26A0\uFE0F Pending: \u20B9${dueAmt.toLocaleString()} (${due.length} tenants)\n\uD83D\uDD27 Open maintenance: ${openM}\n\uD83D\uDCC4 Expiring leases: ${expL}`, icon: "grid" };
  }

  // --- Highest rent / lowest rent ---
  if (ql.match(/highest.*rent|most.*expensive|top.*rent/)) {
    const sorted = [...props].sort((a, b) => (b.rent || 0) - (a.rent || 0)).slice(0, 5);
    return { text: `**Top ${sorted.length} highest rent properties:**\n\n${sorted.map((p, i) => `${i + 1}. ${p.addr} \u2014 \u20B9${(p.rent || 0).toLocaleString()}/mo`).join("\n")}`, icon: "dollar" };
  }

  if (ql.match(/lowest.*rent|cheapest|least.*rent/)) {
    const sorted = [...props].filter(p => p.rent > 0).sort((a, b) => (a.rent || 0) - (b.rent || 0)).slice(0, 5);
    return { text: `**Top ${sorted.length} lowest rent properties:**\n\n${sorted.map((p, i) => `${i + 1}. ${p.addr} \u2014 \u20B9${(p.rent || 0).toLocaleString()}/mo`).join("\n")}`, icon: "dollar" };
  }

  // --- Help ---
  if (ql.match(/help|what can you|commands|options/)) {
    return { text: `**I can answer questions about your data:**\n\n**Properties:**\n\u2022 "Vacant properties"\n\u2022 "How many properties?"\n\u2022 "Highest rent"\n\n**Tenants:**\n\u2022 "Unverified tenants"\n\u2022 "Vacate requests"\n\u2022 "How many tenants?"\n\n**Payments:**\n\u2022 "Overdue rent"\n\u2022 "Revenue this month"\n\u2022 "Pending approvals"\n\n**Other:**\n\u2022 "Open maintenance"\n\u2022 "Expiring leases"\n\u2022 "Summary"`, icon: "grid" };
  }

  // --- Fallback ---
  return { text: `I couldn't understand that. Try asking:\n\u2022 "Show vacant properties"\n\u2022 "Who has overdue rent?"\n\u2022 "Revenue this month"\n\u2022 "Open maintenance"\n\u2022 "Summary"\n\nOr type **help** for all commands.`, icon: "search" };
}

// Simple markdown-like bold rendering
function RichText({ text, h }) {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return <>{parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} style={{ color: h.tx, fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  })}</>;
}

// Parse a message line — if it's a bullet command like • "Vacant properties", return the command text
function parseBulletCommand(line) {
  const m = line.match(/^[•\-]\s+"(.+)"$/);
  return m ? m[1] : null;
}

export default function AIAssistantPage() {
  const { t, h, go, props, tens, pays, mts, leases, ccs } = useContext(Ctx);
  const [messages, setMessages] = useState([
    { role: "ai", text: `Hi! I'm your Smart Assistant. Ask me anything about your properties, tenants, payments, or maintenance.\n\nTry: **"summary"** or **"who has overdue rent?"**`, icon: "grid" }
  ]);
  const [input, setInput] = useState("");
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const quickActions = [
    { label: "📊 Summary", q: "summary" },
    { label: "🔴 Overdue", q: "overdue rent" },
    { label: "🏠 Vacant", q: "vacant properties" },
    { label: "🔧 Maintenance", q: "open maintenance" },
    { label: "❓ Help", q: "help" },
  ];

  const handleSend = (q) => {
    const query = q || input.trim();
    if (!query) return;

    const userMsg = { role: "user", text: query };
    const result = processQuery(query, { props, tens, pays, mts, leases, ccs, t });
    const aiMsg = { role: "ai", text: result.text, icon: result.icon };

    setMessages(prev => [...prev, userMsg, aiMsg]);
    setInput("");
  };

  // Render a single AI message line — clickable chip if it's a bullet command
  const renderLine = (line, j, isUser) => {
    const cmd = !isUser ? parseBulletCommand(line) : null;
    if (cmd) {
      return (
        <div key={j} style={{ marginTop: 4 }}>
          <button
            onClick={() => handleSend(cmd)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: `6px 12px`, borderRadius: rad.pill,
              border: `1.5px solid ${h.ac}`, background: `${h.ac}10`,
              color: h.ac, fontSize: 12, fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s",
              lineHeight: 1.3,
            }}
            onMouseOver={e => { e.currentTarget.style.background = `${h.ac}20`; }}
            onMouseOut={e => { e.currentTarget.style.background = `${h.ac}10`; }}
          >
            <span style={{ fontSize: 10 }}>▶</span>
            {cmd}
          </button>
        </div>
      );
    }
    return (
      <div key={j} style={{ fontSize: 13, lineHeight: 1.5, minHeight: line ? undefined : 8 }}>
        <RichText text={line} h={isUser ? { tx: "#fff" } : h} />
      </div>
    );
  };

  return <div style={{ paddingTop: sp.lg, paddingBottom: 80 }} className="animate-fadeIn">
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
      <Ic n="arrow-left" s={14} c={h.ac} /> {t.back || "Back"}
    </button>

    <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.lg }}>
      <div style={{ ...icBox(h.ac, 40), background: `linear-gradient(135deg, ${h.ac}, ${h.pp || h.ac})` }}>
        <Ic n="zap" s={20} c="#fff" />
      </div>
      <div>
        <h2 style={{ ...type.h1, color: h.tx, margin: 0 }}>AI Assistant</h2>
        <p style={{ ...type.xs, color: h.ts, margin: 0 }}>Tap a command or type your question</p>
      </div>
    </div>

    {/* Quick Actions */}
    <div className="hide-scrollbar" style={{ display: "flex", gap: sp.xs, marginBottom: sp.lg, overflowX: "auto", paddingBottom: 2 }}>
      {quickActions.map(a => (
        <button key={a.q} onClick={() => handleSend(a.q)}
          style={{ padding: `${sp.xs + 2}px ${sp.md}px`, borderRadius: rad.pill, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, fontSize: 11, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
          {a.label}
        </button>
      ))}
    </div>

    {/* Chat Messages */}
    <div style={{ display: "flex", flexDirection: "column", gap: sp.sm }}>
      {messages.map((msg, i) => (
        <div key={i} style={{
          display: "flex", gap: sp.sm,
          flexDirection: msg.role === "user" ? "row-reverse" : "row",
          alignItems: "flex-start",
        }}>
          {msg.role === "ai" && <div style={icBox(h.ac, 28)}>
            <Ic n={msg.icon || "zap"} s={14} c={h.ac} />
          </div>}
          <div style={{
            ...crd(h),
            maxWidth: "85%",
            padding: `${sp.sm}px ${sp.md}px`,
            background: msg.role === "user" ? h.ac : h.cd,
            color: msg.role === "user" ? "#fff" : h.tx,
            borderRadius: msg.role === "user" ? `${rad.md}px ${rad.md}px 4px ${rad.md}px` : `${rad.md}px ${rad.md}px ${rad.md}px 4px`,
            border: msg.role === "user" ? "none" : `1px solid ${h.bd}`,
            boxShadow: msg.role === "user" ? "none" : h.sh,
          }}>
            {msg.text.split("\n").map((line, j) => renderLine(line, j, msg.role === "user"))}
          </div>
        </div>
      ))}
      <div ref={chatEndRef} />
    </div>

    {/* Input Bar */}
    <div style={{
      position: "fixed", bottom: 56, left: "50%", transform: "translateX(-50%)",
      width: "100%", maxWidth: 430, padding: `${sp.sm}px ${sp.md}px`,
      background: h.bg, borderTop: `1px solid ${h.bd}`,
      display: "flex", gap: sp.sm, alignItems: "center", zIndex: 40,
    }}>
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") handleSend(); }}
        placeholder="Ask about your data..."
        style={{
          flex: 1, padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.pill,
          border: `1px solid ${h.bd}`, background: h.cd, color: h.tx,
          fontSize: 14, outline: "none",
        }}
      />
      <button onClick={() => handleSend()} disabled={!input.trim()}
        style={{
          width: 36, height: 36, borderRadius: 18, border: "none",
          background: input.trim() ? h.ac : h.ib, cursor: input.trim() ? "pointer" : "default",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
        <Ic n="send" s={16} c={input.trim() ? "#fff" : h.tt} />
      </button>
    </div>
  </div>;
}
